"use client";

import Link from "next/link";
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { usePageTitle } from "@/lib/usePageTitle";
import { useServerProvisioningContext } from "@/lib/billing/serverProvisioningContext";

// ─── Helpers ───────────────────────────────────────────────────────────────────

function tierLabel(tier: string): string {
  switch (tier) {
    case "starter": return "Founder Starter";
    case "plus": return "Founder Plus";
    case "pro": return "Founder Pro";
    case "enterprise": return "Enterprise";
    default: return tier.charAt(0).toUpperCase() + tier.slice(1);
  }
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function timeSince(ts: number): string {
  const diff = Date.now() - ts;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 2) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return formatDate(ts);
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function AccessStatusBadge({ status }: { status: string }) {
  if (status === "active") {
    return (
      <Badge variant="success" className="text-xs">
        Active
      </Badge>
    );
  }
  if (status === "revoked") {
    return (
      <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-400">
        Revoked
      </span>
    );
  }
  return (
    <span className="rounded-full bg-foreground/10 px-2.5 py-0.5 text-xs font-semibold text-muted">
      {status}
    </span>
  );
}

function ProvisionStatusBadge({ status }: { status: string }) {
  switch (status) {
    case "active":
      return (
        <Badge variant="success" className="text-xs">
          Online
        </Badge>
      );
    case "pending":
      return (
        <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
          Provisioning
        </span>
      );
    case "error":
      return (
        <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-400">
          Error
        </span>
      );
    default:
      return (
        <span className="rounded-full bg-foreground/10 px-2.5 py-0.5 text-xs font-semibold text-muted">
          {status}
        </span>
      );
  }
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 text-sm border-b border-border last:border-0">
      <span className="text-muted">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function DashboardServersPage() {
  usePageTitle("My Servers");

  const { isViewOnly, trialDaysRemaining, serverSlotLimit, isFreeTrial } =
    useServerProvisioningContext();

  const accessRecords = useQuery(api.serverAccess.getMyServerAccess);
  const provisionedServers = useQuery(api.vultr.getMyProvisionedServers);

  const isLoading = accessRecords === undefined || provisionedServers === undefined;

  const activeAccess = accessRecords?.find((r) => r.status === "active") ?? null;
  const latestAccess = accessRecords?.[0] ?? null;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">My Servers</h1>
          <p className="mt-1 text-sm text-muted">
            {isViewOnly
              ? `Free trial — view only. ${trialDaysRemaining !== null ? `${trialDaysRemaining} day${trialDaysRemaining !== 1 ? "s" : ""} remaining.` : ""} Upgrade to provision and manage servers.`
              : "View your server plan, provisioned instances, and connection details."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isViewOnly ? (
            <Link href="/pricing?upgrade=1&reason=trial_view_only&source=servers_page" className="text-sm font-medium text-primary hover:underline">
              Upgrade Now
            </Link>
          ) : (
            <Link href="/dashboard/subscription" className="text-sm text-primary hover:underline">
              Manage Plan
            </Link>
          )}
          <Link href="/dashboard" className="text-sm text-muted hover:underline">
            Dashboard
          </Link>
        </div>
      </div>

      {/* ── Server Plan Access Card ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Server Plan Access</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-5 w-64" />
              <Skeleton className="h-5 w-40" />
            </div>
          ) : activeAccess ? (
            <div>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <span className="text-xl font-bold">{tierLabel(activeAccess.tier)}</span>
                <AccessStatusBadge status={activeAccess.status} />
              </div>
              <div className="divide-y divide-border rounded-lg border border-border">
                <InfoRow label="Plan" value={tierLabel(activeAccess.tier)} />
                <InfoRow
                  label="Access Status"
                  value={<AccessStatusBadge status={activeAccess.status} />}
                />
                <InfoRow
                  label="Granted"
                  value={formatDate(activeAccess.grantedAt)}
                />
                {activeAccess.currentPeriodEnd && (
                  <InfoRow
                    label="Access Valid Until"
                    value={formatDate(activeAccess.currentPeriodEnd)}
                  />
                )}
                <InfoRow
                  label="Subscription ID"
                  value={
                    <code className="rounded bg-foreground/5 px-1.5 py-0.5 text-xs font-mono">
                      {activeAccess.polarSubscriptionId.slice(0, 12)}…
                    </code>
                  }
                />
              </div>
            </div>
          ) : latestAccess ? (
            <div>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <span className="text-xl font-bold">{tierLabel(latestAccess.tier)}</span>
                <AccessStatusBadge status={latestAccess.status} />
              </div>
              <p className="mb-4 text-sm text-muted">
                Your server access is currently {latestAccess.status}
                {latestAccess.revokedReason ? ` (${latestAccess.revokedReason})` : ""}.
                Re-subscribe to restore access.
              </p>
              <Link href="/dashboard/subscription">
                <Button size="sm">Re-subscribe</Button>
              </Link>
            </div>
          ) : (
            <div className="py-8 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-foreground/5">
                <svg
                  className="h-6 w-6 text-muted"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M5 12h14M12 5l7 7-7 7"
                  />
                </svg>
              </div>
              {isFreeTrial ? (
                <>
                  <p className="text-sm font-medium text-foreground">Free Trial — View Only</p>
                  <p className="mt-1 text-sm text-muted">
                    Server provisioning is locked during the free trial. Upgrade to activate
                    {serverSlotLimit > 0 ? ` up to ${serverSlotLimit} server slot${serverSlotLimit !== 1 ? "s" : ""}` : " dedicated game servers"}.
                  </p>
                  <Link
                    href="/pricing?upgrade=1&reason=trial_view_only&source=servers_empty_state"
                    className="mt-4 inline-block"
                  >
                    <Button size="sm">Upgrade to Unlock</Button>
                  </Link>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-foreground">No server plan active</p>
                  <p className="mt-1 text-sm text-muted">
                    Subscribe to get a dedicated game server provisioned for your account.
                  </p>
                  <Link href="/dashboard/subscription" className="mt-4 inline-block">
                    <Button size="sm">View Plans</Button>
                  </Link>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Provisioned Instances Card ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Provisioned Instances</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-20" />
            </div>
          ) : provisionedServers && provisionedServers.length > 0 ? (
            <div className="space-y-4">
              {provisionedServers.map((server) => (
                <div
                  key={server.vultrInstanceId}
                  className="rounded-lg border border-border p-4"
                >
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">
                        {tierLabel(server.subscriptionTier)} Server
                      </span>
                      <ProvisionStatusBadge status={server.provisionStatus} />
                    </div>
                    <span className="text-xs text-muted">
                      Provisioned {timeSince(server.provisionedAt)}
                    </span>
                  </div>

                  <div className="divide-y divide-border rounded-md border border-border bg-foreground/[0.02]">
                    <InfoRow label="Status" value={<ProvisionStatusBadge status={server.provisionStatus} />} />
                    <InfoRow
                      label="IP Address"
                      value={
                        server.serverIp ? (
                          <code className="rounded bg-foreground/5 px-1.5 py-0.5 text-xs font-mono">
                            {server.serverIp}
                          </code>
                        ) : (
                          <span className="text-muted">Pending assignment</span>
                        )
                      }
                    />
                    <InfoRow label="Region" value={server.vultrRegion.toUpperCase()} />
                    <InfoRow label="Plan" value={server.vultrPlanSku} />
                    {server.activatedAt && (
                      <InfoRow label="Activated" value={formatDate(server.activatedAt)} />
                    )}
                  </div>

                  {server.provisionStatus === "pending" && (
                    <p className="mt-3 text-xs text-blue-600 dark:text-blue-400">
                      Your server is being provisioned. This usually takes 2–5 minutes.
                      The IP address will appear once the instance is ready.
                    </p>
                  )}
                  {server.provisionStatus === "error" && (
                    <p className="mt-3 text-xs text-red-600 dark:text-red-400">
                      There was an error provisioning your server. Please contact support.
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <p className="text-sm text-muted">
                {activeAccess
                  ? "Your server is being set up. Check back in a few minutes."
                  : isFreeTrial
                  ? "Server provisioning is unavailable during the free trial. Upgrade to provision your first server."
                  : "No provisioned servers yet. Subscribe to a plan to get started."}
              </p>
              {!activeAccess && (
                <Link
                  href={
                    isFreeTrial
                      ? "/pricing?upgrade=1&reason=trial_view_only&source=servers_instances_empty"
                      : "/pricing"
                  }
                  className="mt-4 inline-block"
                >
                  <Button size="sm" variant="outline">
                    {isFreeTrial ? "Upgrade to Unlock" : "View Pricing"}
                  </Button>
                </Link>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── All Access History ── */}
      {accessRecords && accessRecords.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Access History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {accessRecords.map((record, idx) => (
                <div
                  key={`${record.polarSubscriptionId}-${idx}`}
                  className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <AccessStatusBadge status={record.status} />
                    <span className="text-foreground">{tierLabel(record.tier)}</span>
                  </div>
                  <span className="text-xs text-muted">
                    Granted {formatDate(record.grantedAt)}
                    {record.revokedAt ? ` · Revoked ${formatDate(record.revokedAt)}` : ""}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
